import { INDEX_PAGE_SIZE } from '@shared/lib/pagination';
import { useEffect, useMemo, type Dispatch, type SetStateAction } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Flex, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { FiltersBlock } from '@shared/ui';
import { getFilterEndOfDay, getFilterStartOfDay } from '@shared/helpers';
import { CandidateSelect, useGetCandidates } from '@entities/candidates';
import { CompanySelect, useGetCompanies } from '@entities/company';
import { PaginateType } from '@shared/types';
import {
  REVIEWS_FILTERS_INITIAL,
  type ReviewsFiltersForm,
  type ReviewsListFilter,
} from './types';

type ReviewsFiltersProps = {
  setFilter: Dispatch<SetStateAction<ReviewsListFilter>>;
  preserveParams?: Record<string, string | null | undefined>;
};

export const ReviewsFilters = ({
  setFilter,
  preserveParams = {},
}: ReviewsFiltersProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { data: companies } = useGetCompanies({
    filter: {},
    paginate: PaginateType.NONE,
  });
  const { data: candidates } = useGetCandidates({
    filter: {},
    paginate: PaginateType.NONE,
  });

  const companiesById = useMemo(() => {
    const map = new Map<string, string>();
    const list = (Array.isArray(companies) ? companies : companies?.data) as
      | { id: number; name: string }[]
      | undefined;
    list?.forEach(item => map.set(item.id.toString(), item.name));
    return map;
  }, [companies]);

  const candidatesById = useMemo(() => {
    const map = new Map<
      string,
      { firstName: string | null; lastName: string | null }
    >();
    const list = (Array.isArray(candidates) ? candidates : candidates?.data) as
      | { id: number; firstName: string | null; lastName: string | null }[]
      | undefined;
    list?.forEach(item =>
      map.set(item.id.toString(), {
        firstName: item.firstName,
        lastName: item.lastName,
      })
    );
    return map;
  }, [candidates]);

  const form = useForm<ReviewsFiltersForm>({
    initialValues: REVIEWS_FILTERS_INITIAL,
    validate: {
      reviewAtTo: (value, values) => {
        if (!value || !values.reviewAtFrom) {
          return null;
        }

        return value < values.reviewAtFrom
          ? 'Дата «До» должна быть не раньше даты «От»'
          : null;
      },
    },
  });

  const buildFilter = (values: ReviewsFiltersForm): ReviewsListFilter => {
    const next: ReviewsListFilter = {};

    if (values.reviewAtFrom) {
      next.reviewAtFrom = getFilterStartOfDay(values.reviewAtFrom);
    }
    if (values.reviewAtTo) {
      next.reviewAtTo = getFilterEndOfDay(values.reviewAtTo);
    }

    if (values.companyId) {
      const companyName = companiesById.get(values.companyId);
      if (companyName) {
        next.company = { items: [companyName], operator: 'or' };
      }
    }

    if (values.vacancy.trim()) {
      next.vacancy = { items: [values.vacancy.trim()], operator: 'or' };
    }

    if (values.candidateId) {
      const candidate = candidatesById.get(values.candidateId);
      if (candidate?.firstName) {
        next.candidateFirstname = {
          items: [candidate.firstName],
          operator: 'or',
        };
      }
      if (candidate?.lastName) {
        next.candidateLastname = {
          items: [candidate.lastName],
          operator: 'or',
        };
      }
    }

    return next;
  };

  useEffect(() => {
    form.setValues({
      reviewAtFrom: searchParams.get('reviewAtFrom')
        ? new Date(searchParams.get('reviewAtFrom') as string)
        : null,
      reviewAtTo: searchParams.get('reviewAtTo')
        ? new Date(searchParams.get('reviewAtTo') as string)
        : null,
      candidateId: searchParams.get('candidateId'),
      companyId: searchParams.get('companyId'),
      vacancy: searchParams.get('vacancy') ?? '',
    });
  }, [searchParams]);

  useEffect(() => {
    const values: ReviewsFiltersForm = {
      reviewAtFrom: searchParams.get('reviewAtFrom')
        ? new Date(searchParams.get('reviewAtFrom') as string)
        : null,
      reviewAtTo: searchParams.get('reviewAtTo')
        ? new Date(searchParams.get('reviewAtTo') as string)
        : null,
      candidateId: searchParams.get('candidateId'),
      companyId: searchParams.get('companyId'),
      vacancy: searchParams.get('vacancy') ?? '',
    };

    const hasFilterParams = Boolean(
      values.reviewAtFrom ||
      values.reviewAtTo ||
      values.candidateId ||
      values.companyId ||
      values.vacancy.trim()
    );

    if (!hasFilterParams) {
      setFilter({});
      return;
    }

    if (values.companyId && !companiesById.has(values.companyId)) {
      return;
    }
    if (values.candidateId && !candidatesById.has(values.candidateId)) {
      return;
    }

    setFilter(buildFilter(values));
  }, [searchParams, companiesById, candidatesById]);

  const writeSearchParams = (values: ReviewsFiltersForm) => {
    const next = new URLSearchParams();

    Object.entries(preserveParams).forEach(([key, value]) => {
      if (value) {
        next.set(key, value);
      }
    });

    if (values.reviewAtFrom) {
      next.set('reviewAtFrom', getFilterStartOfDay(values.reviewAtFrom));
    }
    if (values.reviewAtTo) {
      next.set('reviewAtTo', getFilterEndOfDay(values.reviewAtTo));
    }
    if (values.candidateId) {
      next.set('candidateId', values.candidateId);
    }
    if (values.companyId) {
      next.set('companyId', values.companyId);
    }
    if (values.vacancy.trim()) {
      next.set('vacancy', values.vacancy.trim());
    }

    next.set('opened', 'true');
    next.set('page', '1');
    next.set('perPage', searchParams.get('perPage') || String(INDEX_PAGE_SIZE));

    navigate({ search: next.toString() }, { replace: true });
  };

  const handleSubmit = (values: ReviewsFiltersForm) => {
    if (form.validate().hasErrors) {
      return;
    }

    writeSearchParams(values);
    setFilter(buildFilter(values));
  };

  const handleReset = () => {
    form.setValues(REVIEWS_FILTERS_INITIAL);
    setFilter({});

    const next = new URLSearchParams();
    Object.entries(preserveParams).forEach(([key, value]) => {
      if (value) {
        next.set(key, value);
      }
    });
    next.set('page', '1');
    next.set('perPage', searchParams.get('perPage') || String(INDEX_PAGE_SIZE));
    navigate({ search: next.toString() }, { replace: true });
  };

  const activeFiltersCount = [
    form.values.reviewAtFrom,
    form.values.reviewAtTo,
    form.values.candidateId,
    form.values.companyId,
    form.values.vacancy.trim() || null,
  ].filter(Boolean).length;

  return (
    <FiltersBlock
      onSubmit={form.onSubmit(handleSubmit)}
      onReset={handleReset}
      activeFiltersCount={activeFiltersCount}
      defaultOpened={searchParams.get('opened') === 'true'}
    >
      <Flex wrap='wrap' gap={16} rowGap={16}>
        <DatePickerInput
          label='Дата создания, От'
          value={form.values.reviewAtFrom}
          onChange={value => form.setFieldValue('reviewAtFrom', value)}
          error={form.errors.reviewAtFrom}
          placeholder='Выберите'
          miw={245}
          w={245}
          variant='filled'
          maxDate={form.values.reviewAtTo ?? undefined}
          onKeyDown={event => event.preventDefault()}
          clearable
        />

        <DatePickerInput
          label='Дата создания, До'
          value={form.values.reviewAtTo}
          onChange={value => form.setFieldValue('reviewAtTo', value)}
          error={form.errors.reviewAtTo}
          placeholder='Выберите'
          miw={245}
          w={245}
          variant='filled'
          minDate={form.values.reviewAtFrom ?? undefined}
          onKeyDown={event => event.preventDefault()}
          clearable
        />

        <CandidateSelect
          label='Кандидат'
          placeholder='Все'
          value={form.values.candidateId}
          onChange={value => form.setFieldValue('candidateId', value)}
          clearable
          miw={245}
          w={245}
          variant='filled'
        />

        <TextInput
          label='Вакансия'
          placeholder='Все'
          value={form.values.vacancy}
          onChange={event =>
            form.setFieldValue('vacancy', event.currentTarget.value)
          }
          miw={245}
          w={245}
          variant='filled'
        />

        <CompanySelect
          label='Компания'
          placeholder='Все'
          value={form.values.companyId}
          onChange={value => form.setFieldValue('companyId', value)}
          clearable
          miw={245}
          w={245}
          variant='filled'
        />
      </Flex>
    </FiltersBlock>
  );
};
